import { Transaction, TransactionFormData } from '@/types';
import { 
  saveTransactionToSupabase, 
  deleteTransactionFromSupabase 
} from '@/lib/supabase-operations';
import { createTransaction as createTransactionUtil } from './transactionOperations';
import { logger } from '@/utils/logger';
import { TransactionsAction } from './reducer';

export async function addTransactionOperation(
  transactionData: TransactionFormData,
  dispatch: React.Dispatch<TransactionsAction>
): Promise<Transaction> {
  logger.info('Adding transaction', { type: transactionData.type });
  
  dispatch({ type: 'SET_ERROR', payload: null });
  
  try {
    const newTransaction = createTransactionUtil(transactionData);
    logger.debug('Transaction created', { id: newTransaction.id });
    
    // Save to Supabase with retry logic
    let retryCount = 0;
    const maxRetries = 3;
    
    while (retryCount < maxRetries) {
      try {
        await saveTransactionToSupabase(newTransaction);
        logger.debug('Transaction saved to Supabase', { id: newTransaction.id });
        break;
      } catch (saveError) {
        retryCount++;
        logger.warn(`Save attempt ${retryCount} failed`, { error: saveError });
        
        if (retryCount >= maxRetries) {
          throw saveError;
        }
        
        await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
      }
    }
    
    dispatch({ type: 'ADD_TRANSACTION', payload: newTransaction });
    logger.info('Transaction added successfully', { id: newTransaction.id });
    
    return newTransaction;
  } catch (err) {
    logger.error('Error adding transaction', { error: err });
    const errorMessage = err instanceof Error ? err.message : 'Erro ao criar transação';
    dispatch({ type: 'SET_ERROR', payload: errorMessage });
    throw err;
  }
}

export async function updateTransactionOperation(
  id: string,
  updates: Partial<Transaction>,
  transactions: Transaction[],
  dispatch: React.Dispatch<TransactionsAction>
): Promise<void> {
  dispatch({ type: 'SET_ERROR', payload: null });
  
  try {
    const existingTransaction = transactions.find(t => t.id === id);
    if (!existingTransaction) throw new Error('Transaction not found');

    const updatedTransaction = { ...existingTransaction, ...updates };
    
    await saveTransactionToSupabase(updatedTransaction);
    dispatch({ type: 'UPDATE_TRANSACTION', payload: { id, updates } });
    
    logger.info('Transaction updated successfully', { id });
  } catch (err) {
    logger.error('Error updating transaction', { error: err, id });
    const errorMessage = err instanceof Error ? err.message : 'Erro ao atualizar transação';
    dispatch({ type: 'SET_ERROR', payload: errorMessage });
    throw err;
  }
}

export async function deleteTransactionOperation(
  id: string,
  dispatch: React.Dispatch<TransactionsAction>
): Promise<void> {
  dispatch({ type: 'SET_ERROR', payload: null });
  
  try {
    await deleteTransactionFromSupabase(id);
    dispatch({ type: 'DELETE_TRANSACTION', payload: id });
    
    logger.info('Transaction deleted successfully', { id });
  } catch (err) {
    logger.error('Error deleting transaction', { error: err, id });
    const errorMessage = err instanceof Error ? err.message : 'Erro ao deletar transação';
    dispatch({ type: 'SET_ERROR', payload: errorMessage });
    throw err;
  }
}
